import React, {createContext, useEffect, useState} from 'react';
import {useSelector} from '@utils/hooks/common';
import {ThemeMode} from '@types';

export const ThemeContext = createContext<ThemeMode>('system');

export const ThemeProvider: React.FC<{children: React.ReactNode}> = ({
    children,
}) => {
    const {user: authUser} = useSelector(state => state.auth);
    const [theme, setTheme] = useState<ThemeMode>(authUser?.theme || 'system');

    useEffect(() => {
        const savedTheme: ThemeMode = authUser?.theme || 'system';
        const colorSchemeQuery = window.matchMedia(
            '(prefers-color-scheme: dark)'
        );

        const applyTheme = (mode: ThemeMode) => {
            let applied: 'light' | 'dark';

            if (mode === 'dark') {
                applied = 'dark';
            } else if (mode === 'light') {
                applied = 'light';
            } else {
                applied = colorSchemeQuery.matches ? 'dark' : 'light';
            }

            // Clear the previous surface utilities, including during hot updates.
            document.body.classList.remove('bg-dark', 'text-light', 'bg-light', 'text-dark');
            document.documentElement.setAttribute('data-theme', applied);
            document.body.setAttribute('data-theme', applied);
            // Tailwind dark mode class
            if (applied === 'dark') {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }

            setTheme(applied);
        };

        applyTheme(savedTheme);

        const onChange = (e: MediaQueryListEvent) => {
            if ((authUser?.theme || 'system') === 'system') {
                applyTheme(e.matches ? 'dark' : 'light');
            }
        };

        if (savedTheme === 'system') {
            colorSchemeQuery.addEventListener('change', onChange);
        }

        return () => {
            colorSchemeQuery.removeEventListener('change', onChange);
        };
    }, [authUser?.theme]);

    return (
        <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
    );
};
